import { extendTheme } from "@chakra-ui/react";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

export const theme = extendTheme({
  config,
  colors: {
    brand: {
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
    },
  },
  fonts: {
    heading:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
  },
  lineHeights: {
    shorter: 1.25,
    short: 1.375,
    base: 1.5,
    tall: 1.625,
  },
  radii: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(15 23 42 / 0.04)",
    sm: "0 1px 3px 0 rgb(15 23 42 / 0.06), 0 1px 2px -1px rgb(15 23 42 / 0.06)",
    outline: "0 0 0 3px rgb(59 130 246 / 0.35)",
  },
  styles: {
    global: {
      body: {
        bg: "gray.50",
        color: "gray.800",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      },
    },
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: "brand",
        variant: "solid",
      },
      baseStyle: {
        fontWeight: "semibold",
        borderRadius: "lg",
        letterSpacing: "0.01em",
        transitionProperty:
          "background-color,border-color,color,box-shadow,transform",
        transitionDuration: "0.2s",
        transitionTimingFunction: "ease-out",
        _focusVisible: {
          boxShadow: "outline",
        },
        _disabled: {
          opacity: 1,
          cursor: "not-allowed",
          bg: "gray.200",
          color: "gray.500",
        },
      },
      variants: {
        solid: (props) => {
          const c = props.colorScheme || "brand";
          const solidMap = {
            brand: {
              bg: "brand.600",
              color: "white",
              _hover: { bg: "brand.700" },
              _active: { bg: "brand.800" },
            },
            blue: {
              bg: "blue.600",
              color: "white",
              _hover: { bg: "blue.700" },
              _active: { bg: "blue.800" },
            },
            red: {
              bg: "red.600",
              color: "white",
              _hover: { bg: "red.700" },
              _active: { bg: "red.800" },
            },
            green: {
              bg: "green.600",
              color: "white",
              _hover: { bg: "green.700" },
              _active: { bg: "green.800" },
            },
            purple: {
              bg: "purple.600",
              color: "white",
              _hover: { bg: "purple.700" },
              _active: { bg: "purple.800" },
            },
            gray: {
              bg: "gray.700",
              color: "white",
              _hover: { bg: "gray.800" },
              _active: { bg: "gray.900" },
            },
            twitter: {
              bg: "cyan.600",
              color: "white",
              _hover: { bg: "cyan.700" },
              _active: { bg: "cyan.800" },
            },
          };
          return (
            solidMap[c] || {
              bg: "brand.600",
              color: "white",
              _hover: { bg: "brand.700" },
              _active: { bg: "brand.800" },
            }
          );
        },
        outline: (props) => {
          const c = props.colorScheme || "brand";
          const focus = { _focusVisible: { boxShadow: "outline" } };
          if (c === "brand" || c === "blue") {
            const isBrand = c === "brand";
            return {
              ...focus,
              border: "2px solid",
              borderColor: isBrand ? "brand.600" : "blue.600",
              bg: "white",
              color: isBrand ? "brand.700" : "blue.700",
              _hover: {
                bg: isBrand ? "brand.50" : "blue.50",
                borderColor: isBrand ? "brand.700" : "blue.700",
                color: isBrand ? "brand.800" : "blue.800",
              },
              _active: {
                bg: isBrand ? "brand.100" : "blue.100",
              },
            };
          }
          if (c === "red") {
            return {
              ...focus,
              border: "2px solid",
              borderColor: "red.600",
              bg: "white",
              color: "red.700",
              _hover: { bg: "red.50", borderColor: "red.700", color: "red.800" },
            };
          }
          return focus;
        },
        ghost: (props) => {
          const c = props.colorScheme || "gray";
          const focus = { _focusVisible: { boxShadow: "outline" } };
          if (c === "brand") {
            return {
              ...focus,
              color: "brand.700",
              bg: "transparent",
              _hover: { bg: "gray.100", color: "brand.800" },
              _active: { bg: "gray.200" },
            };
          }
          if (c === "blue") {
            return {
              ...focus,
              color: "blue.700",
              bg: "transparent",
              _hover: { bg: "blue.50", color: "blue.800" },
              _active: { bg: "blue.100" },
            };
          }
          if (c === "red") {
            return {
              ...focus,
              color: "red.600",
              bg: "transparent",
              _hover: { bg: "red.50", color: "red.800" },
              _active: { bg: "red.100" },
            };
          }
          return {
            ...focus,
            color: "gray.800",
            bg: "transparent",
            _hover: { bg: "gray.100", color: "gray.900" },
            _active: { bg: "gray.200" },
          };
        },
        link: (props) => {
          const c = props.colorScheme || "blue";
          if (c === "brand") {
            return {
              color: "brand.700",
              _hover: { color: "brand.800", textDecoration: "underline" },
            };
          }
          return {
            color: "blue.700",
            _hover: { color: "blue.800", textDecoration: "underline" },
          };
        },
      },
    },
    IconButton: {
      defaultProps: {
        colorScheme: "gray",
        variant: "ghost",
      },
      baseStyle: {
        bg: "transparent",
        borderRadius: "lg",
        transitionProperty:
          "background-color,border-color,color,box-shadow,transform",
        transitionDuration: "0.2s",
        transitionTimingFunction: "ease-out",
      },
      variants: {
        link: {
          bg: "transparent",
          color: "gray.600",
          height: "auto",
          minW: "auto",
          lineHeight: "normal",
          verticalAlign: "baseline",
          _hover: { bg: "transparent", color: "gray.800" },
          _active: { bg: "transparent" },
        },
        solid: (props) => {
          const c = props.colorScheme || "brand";
          const map = {
            brand: {
              bg: "brand.600",
              color: "white",
              _hover: { bg: "brand.700" },
              _active: { bg: "brand.800" },
            },
            blue: {
              bg: "blue.600",
              color: "white",
              _hover: { bg: "blue.700" },
              _active: { bg: "blue.800" },
            },
            red: {
              bg: "red.600",
              color: "white",
              _hover: { bg: "red.700" },
              _active: { bg: "red.800" },
            },
            gray: {
              bg: "gray.700",
              color: "white",
              _hover: { bg: "gray.800" },
              _active: { bg: "gray.900" },
            },
            twitter: {
              bg: "cyan.600",
              color: "white",
              _hover: { bg: "cyan.700" },
              _active: { bg: "cyan.800" },
            },
          };
          return (
            map[c] || {
              bg: "brand.600",
              color: "white",
              _hover: { bg: "brand.700" },
              _active: { bg: "brand.800" },
            }
          );
        },
        ghost: (props) => {
          const c = props.colorScheme || "gray";
          const focus = {
            _focusVisible: { boxShadow: "outline" },
          };
          const clearBg = { bg: "transparent" };
          if (c === "brand") {
            return {
              ...focus,
              ...clearBg,
              color: "brand.700",
              _hover: {
                bg: "transparent",
                color: "brand.800",
              },
              _active: { bg: "transparent", color: "brand.900" },
            };
          }
          if (c === "red") {
            return {
              ...focus,
              ...clearBg,
              color: "red.600",
              _hover: {
                bg: "transparent",
                color: "red.800",
              },
              _active: { bg: "transparent", color: "red.700" },
            };
          }
          if (c === "blue") {
            return {
              ...focus,
              ...clearBg,
              color: "blue.700",
              _hover: {
                bg: "transparent",
                color: "blue.800",
              },
              _active: { bg: "transparent", color: "blue.900" },
            };
          }
          if (c === "twitter") {
            return {
              ...focus,
              ...clearBg,
              color: "cyan.700",
              _hover: {
                bg: "transparent",
                color: "cyan.800",
              },
              _active: { bg: "transparent", color: "cyan.900" },
            };
          }
          return {
            ...focus,
            ...clearBg,
            color: "gray.700",
            _hover: {
              bg: "transparent",
              color: "gray.900",
            },
            _active: { bg: "transparent", color: "gray.800" },
          };
        },
      },
    },
    Input: {
      defaultProps: {
        focusBorderColor: "brand.500",
        borderRadius: "lg",
      },
    },
    Textarea: {
      defaultProps: {
        focusBorderColor: "brand.500",
        borderRadius: "lg",
      },
    },
    Modal: {
      baseStyle: (props) => ({
        dialog: {
          borderRadius: "lg",
          bg: props.colorMode === "dark" ? "gray.800" : "white",
          shadow: "lg",
        },
      }),
    },
    Drawer: {
      baseStyle: {
        dialog: {
          borderRightWidth: "0",
        },
      },
    },
    Table: {
      variants: {
        premium: {
          th: {
            borderBottom: "1px solid",
            borderColor: "gray.200",
            bg: "gray.50",
            fontWeight: "600",
            fontSize: "xs",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            color: "gray.600",
          },
          td: {
            borderBottom: "1px solid",
            borderColor: "gray.100",
          },
        },
      },
    },
  },
});

export default theme;
